import { TFunction } from '@lyearn/i18n';
import { Box, Dot, getDatePickerValue, Stack } from '@lyearn/ui';
import { FilterOption } from '@/components/Filter/types';
import {
  DateTagFilterTypeFragment,
  TagFilterTypeFragment,
} from '@/components/FilterManager/configs/tags/graphql/queries/generated/tagFilter';
import { isDateFilterEmpty } from '@/components/Form/components/Conditions/components/FilterValues/utils';
import {
  DateValue,
  NewTagValues,
  TagsMenuItem,
  TagValues,
} from '@/modules/corporate/modules/tags/types';
import { DateValueType, EntityTagInput, TagTypeEnum } from '@/types/schema';

export const getSelectedTagsPlaceholder = (tag: TagsMenuItem, selectedValues: TagValues) => {
  if (!selectedValues) {
    return <Box className="inline-block truncate">{tag.name}</Box>;
  }

  if ([TagTypeEnum.MultiSelect, TagTypeEnum.User].includes(tag.type)) {
    const selectedMultiselectAndUserValues = selectedValues as string[] | FilterOption[];

    if (selectedMultiselectAndUserValues.length > 1)
      return (
        <Stack alignItems="center" className="truncate">
          <Box className="inline-block truncate">{tag.name}</Box>
          <Dot background="bg-interactive-primary" />
          <Stack className="flex-shrink-0">{selectedMultiselectAndUserValues.length}</Stack>
        </Stack>
      );
  }

  return <Box className="inline-block truncate">{tag.name}</Box>;
};

export const isTagValueEmpty = (tagValue: TagValues, tag?: TagsMenuItem) => {
  if (!tagValue) {
    return true;
  }

  if (Array.isArray(tagValue) && !tagValue.length) {
    return true;
  }

  if (tag?.type === TagTypeEnum.Date) {
    return isDateFilterEmpty(
      tagValue as DateValue,
      (tag as DateTagFilterTypeFragment).includeEndDate,
    );
  }

  return false;
};

export const isTagValueEmptyNew = (tagValue: NewTagValues, tag?: TagFilterTypeFragment) => {
  if (!tagValue) {
    return true;
  }

  if (Array.isArray(tagValue) && !tagValue.length) {
    return true;
  }

  if (tag?.type === TagTypeEnum.Date) {
    return isDateFilterEmpty(
      tagValue as DateValue,
      (tag as DateTagFilterTypeFragment).includeEndDate,
    );
  }

  return false;
};

export const getSelectedTagValuesNew = (
  tag: TagFilterTypeFragment,
  selectedValues: EntityTagInput,
): string[] => {
  if (tag.type === TagTypeEnum.Date) {
    const dateTag = tag as DateTagFilterTypeFragment;
    const selectedDateValue = selectedValues.dateValue;
    if (!selectedDateValue) {
      return [tag.name];
    }

    const datePickerValue = getDatePickerValue({
      startTime: selectedDateValue.startTime,
      endTime: selectedDateValue.endTime,
      includeEndDate: dateTag.includeEndDate,
      includeTime: dateTag.includeTime,
    });

    if (datePickerValue) {
      return [datePickerValue];
    }

    return [tag.name];
  }

  if (tag.type === TagTypeEnum.Number) {
    return [selectedValues.numberValue?.toString() ?? ''];
  }
  if (tag.type === TagTypeEnum.Text) {
    return [selectedValues.textValue ?? ''];
  }

  return [];
};

export const getSeletedTagValues = (
  tag: TagsMenuItem,
  selectedValues: TagValues,
  t: TFunction,
): string[] => {
  if (!selectedValues) {
    return [];
  }

  if ([TagTypeEnum.MultiSelect, TagTypeEnum.SingleSelect, TagTypeEnum.User].includes(tag.type)) {
    return (selectedValues as FilterOption[]).map((item) => item.label);
  }

  if (tag.type === TagTypeEnum.Date) {
    const dateTag = tag as DateTagFilterTypeFragment;
    const selectedDateValue = selectedValues as DateValueType | undefined;
    if (!selectedDateValue) {
      return [tag.name];
    }

    const datePickerValue = getDatePickerValue({
      startTime: selectedDateValue.startTime,
      endTime: selectedDateValue.endTime,
      includeEndDate: dateTag.includeEndDate,
      includeTime: dateTag.includeTime,
    });

    if (datePickerValue) {
      return [datePickerValue];
    }

    return [tag.name];
  }

  return [selectedValues] as string[];
};
